import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Monitor, 
  Wifi, 
  WifiOff, 
  Edit2, 
  Check, 
  X, 
  Trash2, 
  RefreshCw,
  Smartphone,
  Laptop,
  Tv,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConnectedDisplays = ({ pageId }) => {
  const [displays, setDisplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const loadDisplays = async () => {
    try {
      const response = await axios.get(`${API}/health/displays/page/${pageId}`);
      setDisplays(response.data);
    } catch (error) {
      console.error('Failed to load displays:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDisplays();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadDisplays, 30000);
    return () => clearInterval(interval);
  }, [pageId]);

  const startEditing = (display) => {
    setEditingId(display.display_id);
    setEditName(display.display_name || '');
  };

  const saveName = async (displayId) => {
    try {
      await axios.put(`${API}/health/displays/${displayId}/name`, {
        display_name: editName
      });
      toast.success('Display name updated');
      loadDisplays();
    } catch (error) {
      toast.error('Failed to update name');
    } finally {
      setEditingId(null);
      setEditName('');
    }
  };

  const removeDisplay = async (displayId) => {
    if (!window.confirm('Remove this display from the list?')) return;
    
    try {
      await axios.delete(`${API}/health/displays/${displayId}`);
      toast.success('Display removed');
      loadDisplays();
    } catch (error) {
      toast.error('Failed to remove display');
    }
  };

  const getDeviceIcon = (display) => {
    const ua = (display.user_agent || '').toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <Smartphone className="w-5 h-5" />;
    }
    if (ua.includes('smart-tv') || ua.includes('webos') || ua.includes('tizen')) {
      return <Tv className="w-5 h-5" />;
    }
    if (display.screen_width > 1920) {
      return <Monitor className="w-5 h-5" />;
    }
    return <Laptop className="w-5 h-5" />;
  };

  const getOSName = (display) => {
    const ua = display.user_agent || '';
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS') || ua.includes('iPhone')) return 'iOS';
    if (ua.includes('Chrome OS')) return 'Chrome OS';
    return display.os || 'Unknown';
  };

  const getBrowserName = (display) => {
    const ua = display.user_agent || '';
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Opera')) return 'Opera';
    return display.browser || 'Unknown';
  };

  if (loading) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-6 text-center">
          <RefreshCw className="w-6 h-6 animate-spin text-cyan-400 mx-auto" />
          <p className="text-slate-400 mt-2">Loading displays...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Monitor className="w-5 h-5 text-cyan-400" />
            Connected Displays
            <span className="text-sm font-normal text-slate-400">({displays.length})</span>
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={loadDisplays}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {displays.length === 0 ? (
          <div className="text-center py-8">
            <Monitor className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No displays connected</p>
            <p className="text-slate-500 text-sm mt-1">
              Open the slideshow page on a device to see it here
            </p>
          </div>
        ) : (
          displays.map((display, idx) => (
            <div
              key={`${display.display_id}-${idx}`}
              className="bg-slate-950 p-4 rounded-lg border border-slate-800"
            >
              <div className="flex items-start gap-4">
                {/* Status and Device Icon */}
                <div className="flex-shrink-0 flex flex-col items-center gap-2">
                  <div className={`p-2 rounded-lg ${display.status === 'online' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {getDeviceIcon(display)}
                  </div>
                  {display.status === 'online' ? (
                    <Wifi className="w-4 h-4 text-green-400" />
                  ) : (
                    <WifiOff className="w-4 h-4 text-red-400" />
                  )}
                </div>

                {/* Display Info */}
                <div className="flex-1 min-w-0">
                  {/* Name */}
                  {editingId === display.display_id ? (
                    <div className="flex items-center gap-2 mb-2">
                      <Input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Enter display name..."
                        className="bg-slate-900 border-slate-700 text-white text-sm h-8 flex-1"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveName(display.display_id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={() => saveName(display.display_id)}
                        className="h-8 w-8 p-0 bg-green-500 hover:bg-green-600"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingId(null)}
                        className="h-8 w-8 p-0 border-slate-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="flex items-center gap-2 mb-1 cursor-pointer group"
                      onClick={() => startEditing(display)}
                    >
                      <h4 className="text-white font-medium">
                        {display.display_name || 'Unnamed Display'}
                      </h4>
                      <Edit2 className="w-3 h-3 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      display.status === 'online' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {display.status === 'online' ? 'Online' : 'Offline'}
                    </span>
                    <span className="text-slate-500 text-xs">
                      {display.last_seen_text}
                    </span>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <Globe className="w-3 h-3 text-slate-500" />
                      <span className="text-slate-400">IP:</span>
                      <span className="text-slate-300">{display.ip_address || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Monitor className="w-3 h-3 text-slate-500" />
                      <span className="text-slate-400">Resolution:</span>
                      <span className="text-slate-300">
                        {display.screen_width}x{display.screen_height}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Laptop className="w-3 h-3 text-slate-500" />
                      <span className="text-slate-400">OS:</span>
                      <span className="text-slate-300">{getOSName(display)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="w-3 h-3 text-slate-500" />
                      <span className="text-slate-400">Browser:</span>
                      <span className="text-slate-300">{getBrowserName(display)}</span>
                    </div>
                  </div>

                  {/* Currently Playing */}
                  {display.status === 'online' && display.current_item_index !== undefined && (
                    <div className="mt-2 text-xs text-slate-500">
                      Playing item {display.current_item_index + 1} of {display.total_items}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeDisplay(display.display_id)}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default ConnectedDisplays;
